import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SuggestionItem } from '../../types';

type UseSuggestionKeyboardOptions = {
  items: SuggestionItem[];
  query: string;
  onSelect: (item: SuggestionItem) => void;
  onEdit?: (item: SuggestionItem) => void;
  hasEditAction?: boolean;
};

export function useSuggestionKeyboard({
  items,
  query,
  onSelect,
  onEdit,
  hasEditAction = false,
}: UseSuggestionKeyboardOptions) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isEditFocused, setIsEditFocused] = useState(false);

  const lowerQuery = query?.toLowerCase() ?? '';

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
    setIsEditFocused(false);
  }, [lowerQuery]);

  const selectedItem = items[selectedIndex];
  const selectedId = selectedItem?.id;
  const itemsById = useMemo(() => _.keyBy(items, 'id'), [items]);

  const currentItemHasEdit = useCallback(() => {
    return hasEditAction && selectedItem ? selectedItem.type !== 'action' : false;
  }, [hasEditAction, selectedItem]);

  const handleEditAction = useCallback(() => {
    if (selectedItem && onEdit) {
      onEdit(selectedItem);
    }
  }, [selectedItem, onEdit]);

  const handleItemClick = useCallback(
    (id: string) => {
      const item = itemsById[id];
      if (item) onSelect(item);
    },
    [itemsById, onSelect]
  );

  const onKeyDown = useCallback(
    ({ event }: { event: KeyboardEvent }) => {
      if (items.length === 0) return false;

      if (event.key === 'ArrowUp') {
        setIsEditFocused(false);
        setSelectedIndex(prev => (prev + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setIsEditFocused(false);
        setSelectedIndex(prev => (prev + 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowRight' && hasEditAction) {
        if (currentItemHasEdit() && !isEditFocused) {
          setIsEditFocused(true);
          return true;
        }
        return false;
      }
      if (event.key === 'ArrowLeft' && hasEditAction) {
        if (isEditFocused) {
          setIsEditFocused(false);
          return true;
        }
        return false;
      }
      if (event.key === 'Enter') {
        if (isEditFocused && hasEditAction) {
          handleEditAction();
          return true;
        }
        if (selectedItem) onSelect(selectedItem);
        return true;
      }
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          setIsEditFocused(false);
          setSelectedIndex(prev => (prev + items.length - 1) % items.length);
        } else {
          if (isEditFocused && hasEditAction) {
            handleEditAction();
            return true;
          }
          if (selectedItem) onSelect(selectedItem);
        }
        return true;
      }
      return false;
    },
    [
      items,
      selectedItem,
      isEditFocused,
      hasEditAction,
      currentItemHasEdit,
      handleEditAction,
      onSelect,
    ]
  );

  return {
    selectedIndex,
    selectedId,
    isEditFocused,
    onKeyDown,
    handleItemClick,
  };
}
